import axios, { AxiosError, AxiosInstance, AxiosResponse, CancelTokenSource } from 'axios';
import { EventEmitter } from 'events';
import { APIErrorResponse, BaseMessage, Chat, CommunicationTool, FileBean, getGlobal, Message, MessageBodyTypeClass, MessageStatus, MessageType, setGlobal, VideoConferenceMessage } from '.';
import poll from './poll';

const mongoObjectId = function () {
  var timestamp = (new Date().getTime() / 1000 | 0).toString(16);
  return timestamp + 'xxxxxxxxxxxxxxxx'.replace(/[x]/g, function () {
    return (Math.random() * 16 | 0).toString(16);
  }).toLowerCase();
};

let instance: ChatAPI;

class ChatAPI extends EventEmitter {
  public classId?: string;
  public id?: string;
  public accessToken?: string;
  public corporateAccountId: string = getGlobal('REACT_APP_SYDLE_CORPORATE_ACCOUNT_ID');

  public api?: AxiosInstance;
  private messages: Array<Message> = [];
  public chat: Chat | null = null;
  private pooling = false;
  private config: any = null;
  private language = window.navigator.language;

  private CACHE = new Map<string, { url: string, blob: Blob, downloadLink: string }>();
  // Data de atualização mais recente
  protected lastUpdateDate: Date = new Date(0);

  constructor() {
    super();

    if (!instance) {
      this.lastUpdateDate = new Date(0);
      this.api = axios.create({
        baseURL: `${getGlobal('REACT_APP_BASE_URL')}/api/1/${getGlobal('REACT_APP_APPLICATION')}`,
        headers: {
          'Accept-Language': this.language,
          'Content-Type': 'application/json'
        }
      });
      instance = this;
    }

    return instance;
  }

  getFile({ _classId, _objId, _id, contentType }: any) {
    let url = `/_classId/${_classId}/_download/${_objId}/${_id}?contentType=${contentType}`;
    return this.api!.get(url, { data: null, responseType: 'blob' })
      .then((res: AxiosResponse<Blob>) => {
        return {
          url: URL.createObjectURL(res.data),
          blob: res.data,
          downloadLink: `${this.api?.defaults.baseURL}${url}`
        };
      });
  }

  getChat(chat?: any) {
    if (chat) {
      return this.api!.get(`/_classId/${chat._class._id}/getChat/${chat._id}`, { data: null })
        .then((resp: AxiosResponse<{ chat: Chat }>) => {
          if (resp.data.chat.status !== 'completed') {
            this.chat = resp.data.chat;
            this.id = resp.data.chat._id;
            this.classId = resp.data.chat._class._id;
            this.emit('chat', resp.data.chat);

            if (!this.pooling) {
              this.initPoll();
            }

            localStorage.setItem('_chat', JSON.stringify(this.chat));
          }
          return resp.data.chat;
        });
    } else {
      return this.api!.get(`/_classId/${this.classId}/getChat/${this.id}`, { data: null })
        .then((resp: AxiosResponse<{ chat: Chat }>) => {
          this.chat = resp.data.chat;
          this.emit('chat', resp.data.chat);

          if (!this.pooling) {
            this.initPoll();
          }

          localStorage.setItem('_chat', JSON.stringify(this.chat));

          return this.chat;
        });
    }
  }

  initPoll() {
    this.pooling = true;
    poll(this.getMessages.bind(this), () => false, getGlobal('REACT_APP_POOLING_TIME') || 5000);
  }

  addInitialMessage(initialMessage: string, communicationTool: CommunicationTool) {
    this.messages = [{
      body: {
        body: initialMessage
      },
      date: (new Date()).toISOString(),
      type: MessageType.Text,
      status: MessageStatus.Read,
      from: communicationTool,
      _id: '0'
    }];
    this.emit('messages', { messages: this.messages, isOld: false });
  }

  renewToken(token: string, application: string = getGlobal('REACT_APP_APPLICATION')) {
    return axios.get(`${getGlobal('REACT_APP_BASE_URL')}/api/1/${application}/sys/auth/renewToken`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
      .then((res: AxiosResponse) => {
        this.accessToken = res.data.accessToken.token;
        this.api = axios.create({
          baseURL: `${getGlobal('REACT_APP_BASE_URL')}/api/1/${application}`,
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          }
        });
        return res.data;
      }).catch((err: AxiosError<APIErrorResponse>) => {
        return Promise.reject(err.response?.status);
      })
  }

  validateToken(token: string) {
    return axios.get(`${getGlobal('REACT_APP_BASE_URL')}/api/sys/rest/validateToken`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept-Language': this.language,
        'Content-Type': 'application/json'
      }
    }).then((res: AxiosResponse) => {
      let application = getGlobal('REACT_APP_APPLICATION');

      if (res.data.accessToken?.payload?.audiency?.[0]) {
        application = res.data.accessToken?.payload?.audiency[0];
        setGlobal('REACT_APP_APPLICATION', application);
      }

      this.accessToken = res.data.accessToken.token;
      this.api = axios.create({
        baseURL: `${getGlobal('REACT_APP_BASE_URL')}/api/1/${application}`,
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      return res.data;
    });
  }

  getOrgConfig() {
    return axios.get(`${getGlobal('REACT_APP_BASE_URL')}/api/1/${getGlobal('REACT_APP_APPLICATION')}/_organizationConfigs/${getGlobal('REACT_APP_ORGANIZATION')}`)
      .then(res => {
        return res.data;
      })
      .catch((err: AxiosError<APIErrorResponse>) => {
        return Promise.reject(err.response?.status);
      })
  }

  signIn({ user, password }: { user: string, password: string }) {
    const base64 = btoa(`${user}:${password}`);
    return axios.get(`${getGlobal('REACT_APP_BASE_URL')}/api/1/${getGlobal('REACT_APP_APPLICATION')}/sys/auth/signIn`, {
      headers: {
        Authorization: `Basic ${base64}`
      }
    })
      .then((res: AxiosResponse) => {
        this.accessToken = res.data.accessToken.token;
        this.api = axios.create({
          baseURL: `${getGlobal('REACT_APP_BASE_URL')}/api/1/${getGlobal('REACT_APP_APPLICATION')}`,
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          }
        });
        return res.data;
      }).catch((err: AxiosError<APIErrorResponse>) => {
        return Promise.reject(err.response?.status);
      })
  }

  getMessages() {
    let endpoint = `/_classId/${this.classId}/getMessagesFromChat/${this.id}`;
    if (this.lastUpdateDate.getTime() > 0) {
      endpoint += `?lastUpdateDate=${this.lastUpdateDate.toISOString()}`;
    }
    return this.api!.get(endpoint, { data: null })
      .then(res => {
        let hasMessagesChanged = false;
        const oldestMessage = this.messages[0];
        const loadedMessages = [...this.messages];
        // postMessage:
        if (res.data.messages && res.data.messages.length > 0 && res.data.messages[0].postMessage && localStorage.getItem('_lastMessageId') !== res.data.messages[0]._id) {
          localStorage.setItem('_lastMessageId', res.data.messages[0]._id);
          window.parent.postMessage(res.data.messages[0].postMessage, '*');
        }
        (res.data.messages as Message[]).reverse().forEach(newMessage => {
          if (newMessage._lastUpdateDate) {
            const date = new Date(newMessage._lastUpdateDate);
            if (date > this.lastUpdateDate) {
              this.lastUpdateDate = date;
            }
          }

          const index = loadedMessages.map(loadedMessage => loadedMessage._id).indexOf(newMessage._id);
          if (index !== -1) {
            if (newMessage.status !== this.messages[index].status ||
              (newMessage.type === MessageType.VideoConference &&
                (newMessage.body.status !== (this.messages[index] as VideoConferenceMessage).body.status || newMessage.body.videoConference?.status !== (this.messages[index] as VideoConferenceMessage).body.videoConference?.status))) {
              this.messages[index] = newMessage;
              this.messages = [...this.messages];
              hasMessagesChanged = true;
            }
          } else {
            // Verificar se a mensagem está dentro da janela no front
            if (!oldestMessage || new Date(newMessage.date).getTime() > new Date(oldestMessage.date).getTime()) {
              this.messages = [...this.messages, newMessage];
              hasMessagesChanged = true;
            }
          }
        });

        if (hasMessagesChanged) {
          this.emit('messages', { messages: this.messages, isOld: false });
        }

        return res.data;
      })
      .catch((error: AxiosError<APIErrorResponse>) => {
        console.error(error.response?.data)
        return [];
      });
  }

  getOldMessages() {
    const olderMessage = this.messages.sort((a, b) => {
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    })[0];

    return this.api!.get(`/_classId/${this.classId}/getMessagesFromChat/${this.chat?._id}?olderMessageDate=${olderMessage.date}`, { data: null })
      .then(res => {
        let hasMessagesChanged = false;
        const loadedMessages = [...this.messages];
        (res.data.messages as Message[]).reverse().forEach(newMessage => {
          const index = loadedMessages.map(loadedMessage => loadedMessage._id).indexOf(newMessage._id);
          if (index === -1) {
            this.messages = [newMessage, ...this.messages];
            hasMessagesChanged = true;
          }
        });

        if (hasMessagesChanged) {
          this.emit('messages', { messages: this.messages, isOld: true });
        }

        return res.data;
      })
      .catch((error: AxiosError<APIErrorResponse>) => {
        return Promise.reject(error.response?.data);
      });
  }

  uploadFile(file: File, cancelToken?: CancelTokenSource) {
    let formData = new FormData();
    const source = cancelToken;
    const config = {
      headers: { 'content-type': 'multipart/form-data' },
      cancelToken: source?.token
    }
    formData.append('file', file);
    return this.api!.post(`/_classId/${this.classId}/_upload/${this.id}`, formData, config)
      .then((res: AxiosResponse<FileBean>) => {
        return res.data;
      }).catch((error: AxiosError<APIErrorResponse>) => {
        return Promise.reject(error.response?.data);
      });
  }

  getConfig() {
    if (!this.config) {
      return this.api!.post(`/com.sydle.communication/ChatWebAccount/getConfig/${this.corporateAccountId}`, {
        navigator: {
          cookieEnabled: navigator.cookieEnabled,
          language: navigator.language,
          languages: navigator.languages,
          userAgent: navigator.userAgent
        },
        localStorage: localStorage
      }).then((res: any) => {
        this.config = res.data;
        this.config.serverDiff = new Date(this.config.serverDate).getTime() - Date.now();
        return res.data;
      }).catch((error: AxiosError<APIErrorResponse>) => {
        return Promise.reject(error.response?.data);
      });
    } else {
      return Promise.resolve(this.config);
    }
  }

  sendMessage({ from, type, body, sendHash }: { from: any, type: MessageType, body: Message['body'], sendHash?: string }) {
    return this.api!.post(`/com.sydle.communication/ChatWebAccount/inboundNotifications/${this.corporateAccountId}`, {
      from: {
        _id: from.code
      },
      messageBody: {
        _class: MessageBodyTypeClass[type],
        ...body
      },
      type,
      sendHash,
      chatId: this.id,
      additionalData: {
        useragent: navigator.userAgent,
        url: window.location.href,
        host: window.location.host,
        locale: navigator.language,
        env: (window as { [key: string]: any })["ENV"]
      }
    },
      {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        }
      }).then((res: AxiosResponse<Message>) => {
        const index = this.messages.findIndex((message) => message._id === res.data._id);
        if (index === -1) {
          this.messages = [...this.messages, res.data];
        }

        if ((!this.id || !this.classId) || (this.id !== res.data.chat?._id)) {
          const chat = res.data.chat as any;
          this.id = chat._id;
          this.classId = chat._class._id;
          this.getChat();
        }

        this.emit('messages', { messages: this.messages, isOld: false });
        if (this.id) {
          this.getMessages();
        }
        return res.data;
      }).catch((error: AxiosError<APIErrorResponse>) => {
        return Promise.reject(error.response?.data);
      });
  }

  genMessage({ type, body, cancelToken }: { type: MessageType, body: Message['body'], cancelToken: CancelTokenSource }): BaseMessage {
    return {
      _id: mongoObjectId(),
      type,
      body: {
        _class: MessageBodyTypeClass[type],
        ...body
      },
      cancelToken: cancelToken,
      date: new Date(Date.now() + this.config.serverDiff),
      from: {
        user: this.chat?.client!
      },
      status: MessageStatus.Queued,
      sendHash: mongoObjectId()
    };
  }

  async downloadPhoto({ _classId, _objId }: any) {
    let url = `/_classId/5f219106e0c42b6b86a697c8/downloadPhoto?_body=${encodeURIComponent(JSON.stringify({ _classId, _objId }))}`;

    const cached = this.CACHE.get(url);
    if (cached) {
      return cached;
    }

    return await this.api!.get(url, { data: null, responseType: 'blob' })
      .then((res: AxiosResponse<Blob>) => {
        const response = {
          url: URL.createObjectURL(res.data),
          blob: res.data,
          downloadLink: `${this.api?.defaults.baseURL}${url}`
        };
        this.CACHE.set(url, response);
        return response;
      });
  }

  addContext({ key, value }: { key: string, value: string }) {
    return this.api!.post(`/_classId/${this.classId}/addContext/${this.id}`, { key, value });
  }

}

export default ChatAPI;
